/**
 * synth.ts — sounds made from arithmetic (2026-09-20). PURE: every function
 * returns PCM in a Float32Array; the runtime wraps it in an AudioBuffer.
 *
 * WHY: five material impact clips (`sfx_impact_metal|stone|soil|crystal|body`)
 * are referenced by `surface_response.ts` and exist in NO catalog, so the
 * material-aware impact layer has been silent since it shipped — every surface
 * answered with the same generic slug. Generating them costs credits the
 * account does not have; MODAL SYNTHESIS costs nothing and is how struck
 * objects are modelled anyway: a struck body rings at a handful of decaying
 * partials (metal: long, inharmonic; crystal: high and glassy), while a loose
 * one (soil, sand, flesh) is a band of noise that dies at once. A recorded clip
 * installed later under the same key simply wins (`procedural` is the FALLBACK).
 *
 * Also here: the UI click used when `ui_click` cannot be fetched, and the room
 * impulse responses (decaying, progressively darker noise — the standard cheap
 * late-reverb model; there are no IR assets to license or ship).
 *
 * Deterministic: a seeded PRNG, so a harness can pin the output.
 */

/** mulberry32 — small, fast, good enough for noise. */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function normalize(buf: Float32Array, peak: number): Float32Array {
  let m = 0;
  for (let i = 0; i < buf.length; i++) { const a = Math.abs(buf[i]); if (a > m) m = a; }
  if (m > 1e-9) { const k = peak / m; for (let i = 0; i < buf.length; i++) buf[i] *= k; }
  return buf;
}

/** A 2 ms fade at both ends — a buffer that starts or stops off zero clicks. Applied BEFORE normalising: a struck
 *  sound's loudest sample is its first tick, and fading after the normalise would pull the peak under its target. */
function declick(buf: Float32Array, sr: number): Float32Array {
  const n = Math.min(buf.length >> 1, Math.round(sr * 0.002));
  for (let i = 0; i < n; i++) { const k = i / n; buf[i] *= k; buf[buf.length - 1 - i] *= k; }
  return buf;
}

interface Partial_ { hz: number; amp: number; decayS: number }
interface NoiseBand { lowHz: number; highHz: number; amp: number; decayS: number; delayS?: number; /** A soft surface SWELLS into its noise instead of ticking (undefined = instant, as every impact is). */ attackS?: number }

interface ImpactRecipe { seconds: number; partials: Partial_[]; noise: NoiseBand[]; clickAmp: number; grains?: { count: number; spanS: number; amp: number } }

/**
 * The five materials. Frequencies are inharmonic on purpose (a plate or a shard
 * is not a string); decays are what tells metal from stone more than pitch does.
 */
export const IMPACT_RECIPES: Record<string, ImpactRecipe> = {
  // Worked metal: a bright tick, then a ring that outlives everything else in the mix.
  metal: {
    seconds: 0.62, clickAmp: 0.9,
    partials: [{ hz: 512, amp: 0.55, decayS: 0.24 }, { hz: 1187, amp: 0.7, decayS: 0.3 }, { hz: 1934, amp: 0.5, decayS: 0.22 }, { hz: 2877, amp: 0.42, decayS: 0.16 }, { hz: 3921, amp: 0.3, decayS: 0.11 }, { hz: 5310, amp: 0.2, decayS: 0.07 }],
    noise: [{ lowHz: 2500, highHz: 9000, amp: 0.35, decayS: 0.018 }],
  },
  // Stone and concrete: a dry crack, a short thud under it, grit settling.
  stone: {
    seconds: 0.4, clickAmp: 0.7,
    partials: [{ hz: 138, amp: 0.6, decayS: 0.055 }, { hz: 322, amp: 0.3, decayS: 0.035 }],
    noise: [{ lowHz: 900, highHz: 4200, amp: 0.8, decayS: 0.035 }],
    grains: { count: 9, spanS: 0.26, amp: 0.22 },
  },
  // Soil, moss, sand: a dull thump in its own ejecta — almost no highs at all.
  soil: {
    seconds: 0.36, clickAmp: 0.12,
    partials: [{ hz: 82, amp: 0.85, decayS: 0.07 }, { hz: 151, amp: 0.35, decayS: 0.045 }],
    noise: [{ lowHz: 120, highHz: 900, amp: 0.7, decayS: 0.06 }, { lowHz: 1800, highHz: 6000, amp: 0.12, decayS: 0.12, delayS: 0.02 }],
  },
  // Crystal: high, glassy and long; two close partials beat against each other.
  crystal: {
    seconds: 0.9, clickAmp: 0.6,
    partials: [{ hz: 2093, amp: 0.55, decayS: 0.42 }, { hz: 2131, amp: 0.4, decayS: 0.4 }, { hz: 3412, amp: 0.45, decayS: 0.3 }, { hz: 5188, amp: 0.34, decayS: 0.2 }, { hz: 7921, amp: 0.2, decayS: 0.12 }],
    noise: [{ lowHz: 4000, highHz: 12000, amp: 0.3, decayS: 0.012 }],
    grains: { count: 6, spanS: 0.35, amp: 0.12 },
  },
  // Flesh and armour plate: the response belongs to the target — wet, low and over at once.
  body: {
    seconds: 0.3, clickAmp: 0.05,
    partials: [{ hz: 96, amp: 0.9, decayS: 0.06 }, { hz: 188, amp: 0.3, decayS: 0.035 }],
    noise: [{ lowHz: 150, highHz: 650, amp: 0.55, decayS: 0.04 }],
  },
};

/** Band-limited noise by two one-pole filters (a low-pass then a high-pass): cheap, stable, no FFT. */
function bandNoise(out: Float32Array, sr: number, band: NoiseBand, rng: () => number): void {
  const aLp = 1 - Math.exp(-2 * Math.PI * Math.min(band.highHz, sr * 0.45) / sr);
  const aHp = 1 - Math.exp(-2 * Math.PI * band.lowHz / sr);
  let lp = 0, hpState = 0;
  const start = Math.round((band.delayS ?? 0) * sr);
  for (let i = start; i < out.length; i++) {
    const t = (i - start) / sr;
    const decay = Math.exp(-t / band.decayS);
    if (decay < 1e-4) break; // judged on the DECAY alone: an attack-shaped band starts AT zero, and "env is tiny" would end it on its first sample
    const env = band.attackS ? decay * (1 - Math.exp(-t / band.attackS)) : decay;
    lp += aLp * ((rng() * 2 - 1) - lp);
    hpState += aHp * (lp - hpState);
    out[i] += (lp - hpState) * band.amp * env;
  }
}

/** One struck-material impact, mono, peak-normalised. `seed` varies the noise and the grit, not the pitch identity. */
export function synthImpact(material: string, sampleRate: number, seed = 1): Float32Array {
  const r = IMPACT_RECIPES[material] ?? IMPACT_RECIPES.stone;
  const rng = prng(seed * 7919 + material.length * 131);
  const out = new Float32Array(Math.round(r.seconds * sampleRate));
  const detune = 1 + (rng() - 0.5) * 0.06; // ±3 %: the same wall never rings twice at one pitch
  for (const p of r.partials) {
    const w = 2 * Math.PI * p.hz * detune / sampleRate;
    const phase = rng() * Math.PI * 2;
    for (let i = 0; i < out.length; i++) {
      const env = Math.exp(-(i / sampleRate) / p.decayS);
      if (env < 1e-4) break;
      out[i] += Math.sin(w * i + phase) * p.amp * env;
    }
  }
  for (const b of r.noise) bandNoise(out, sampleRate, b, rng);
  // The contact itself: a 1.5 ms broadband tick.
  const clickN = Math.round(sampleRate * 0.0015);
  for (let i = 0; i < clickN && i < out.length; i++) out[i] += (rng() * 2 - 1) * r.clickAmp * (1 - i / clickN);
  // Debris settling: sparse, quiet, late.
  if (r.grains) {
    for (let g = 0; g < r.grains.count; g++) {
      const at = Math.round((0.03 + rng() * r.grains.spanS) * sampleRate);
      const len = Math.round(sampleRate * (0.002 + rng() * 0.004));
      const amp = r.grains.amp * (0.4 + rng() * 0.6) * (1 - (at / sampleRate) / (r.grains.spanS + 0.05));
      for (let i = 0; i < len && at + i < out.length; i++) out[at + i] += (rng() * 2 - 1) * amp * (1 - i / len);
    }
  }
  return normalize(declick(out, sampleRate), 0.85);
}

// ─── Footfalls ───────────────────────────────────────────────────────────────

/**
 * OTHER PEOPLE'S FOOTSTEPS (2026-09-20). A crowd of versonas and every other
 * traveler walked in total silence: the only footsteps in the game were the
 * player's own recorded LOOP. Slicing those loops into one-shots was measured
 * and rejected — only `steps_grass` has silence between its footfalls;
 * `steps_regolith` is one continuous crunch and `steps_stone` sits on a noise
 * floor — so a contact-driven step has nothing clean to play.
 *
 * A footstep is cheap to MODEL, though (Cook 2002, Farnell 2010): a heel strike
 * and, a moment later, the roll onto the ball of the foot — each a low thump
 * plus the surface's own noise — and what tells one ground from another is the
 * noise: a hard tick on stone, a soft swell on grass, a slow low squelch in
 * mud, a long hiss in sand, grit on regolith, a dense creaking crunch in snow.
 * Synthesised, no two steps are alike (seeded variants × the caller's rate
 * jitter), each is placed WHERE the foot landed, and none costs a byte of
 * assets. They are heard quietly, from metres away: a modest model is the
 * right model. The player's own steps stay the recorded loops.
 */
interface FootRecipe {
  seconds: number;
  /** The heel: a low thump, and the hard tick of the contact itself (0 on soft ground). */
  thumpHz: number; thumpAmp: number; thumpDecayS: number; clickAmp: number;
  /** The roll onto the ball of the foot: when, and how loud against the heel. */
  toeDelayS: number; toeLevel: number;
  /** The surface's voice, laid at BOTH contacts. */
  noise: NoiseBand[];
  /** Loose ground answers in grains: band-limited ticks scattered through the step. */
  grains?: { count: number; spanS: number; amp: number; highHz: number };
}

export const FOOT_SURFACES = ['stone', 'grass', 'mud', 'sand', 'regolith', 'snow'] as const;
export type FootSurface = (typeof FOOT_SURFACES)[number];

export const FOOT_RECIPES: Record<FootSurface, FootRecipe> = {
  // Paving: a hard heel tick, a short dry thud, almost nothing after it.
  stone: { seconds: 0.2, thumpHz: 120, thumpAmp: 0.6, thumpDecayS: 0.028, clickAmp: 0.75, toeDelayS: 0.055, toeLevel: 0.45,
    noise: [{ lowHz: 1400, highHz: 6500, amp: 0.4, decayS: 0.012 }] },
  // Grass and leaf litter: no tick at all — a soft rustle that swells and goes.
  grass: { seconds: 0.3, thumpHz: 82, thumpAmp: 0.4, thumpDecayS: 0.04, clickAmp: 0, toeDelayS: 0.085, toeLevel: 0.7,
    noise: [{ lowHz: 1800, highHz: 7500, amp: 0.55, decayS: 0.045, attackS: 0.006 }, { lowHz: 300, highHz: 1200, amp: 0.2, decayS: 0.03 }] },
  // Mud: low, slow and wet — the noise arrives late (the foot sinking) and there are no highs to speak of.
  mud: { seconds: 0.34, thumpHz: 66, thumpAmp: 0.75, thumpDecayS: 0.06, clickAmp: 0, toeDelayS: 0.11, toeLevel: 0.8,
    noise: [{ lowHz: 140, highHz: 850, amp: 0.7, decayS: 0.07, attackS: 0.014 }, { lowHz: 1500, highHz: 3800, amp: 0.1, decayS: 0.03, delayS: 0.03 }] },
  // Sand: a long soft hiss as the grains give way, under a muffled heel.
  sand: { seconds: 0.36, thumpHz: 74, thumpAmp: 0.45, thumpDecayS: 0.05, clickAmp: 0, toeDelayS: 0.1, toeLevel: 0.75,
    noise: [{ lowHz: 2200, highHz: 9000, amp: 0.5, decayS: 0.085, attackS: 0.01 }, { lowHz: 200, highHz: 900, amp: 0.25, decayS: 0.04 }] },
  // Regolith and scree: grit — a firm heel and a scatter of small hard grains rolling under the sole.
  regolith: { seconds: 0.32, thumpHz: 98, thumpAmp: 0.55, thumpDecayS: 0.035, clickAmp: 0.3, toeDelayS: 0.075, toeLevel: 0.6,
    noise: [{ lowHz: 900, highHz: 5200, amp: 0.4, decayS: 0.03 }], grains: { count: 14, spanS: 0.2, amp: 0.4, highHz: 6000 } },
  // Snow: the crunch is compression — dense mid grains through the whole press, and a muffled world around them.
  snow: { seconds: 0.4, thumpHz: 70, thumpAmp: 0.4, thumpDecayS: 0.05, clickAmp: 0, toeDelayS: 0.12, toeLevel: 0.85,
    noise: [{ lowHz: 500, highHz: 3200, amp: 0.45, decayS: 0.07, attackS: 0.012 }], grains: { count: 26, spanS: 0.28, amp: 0.45, highHz: 3600 } },
};

/** One footfall, mono, peak-normalised (0.6 — a foot is a quiet source; the caller's gain does the rest). */
export function synthFootstep(surface: string, sampleRate: number, seed = 1): Float32Array {
  const r = FOOT_RECIPES[surface as FootSurface] ?? FOOT_RECIPES.stone;
  const rng = prng(seed * 6271 + surface.length * 977 + 17);
  const out = new Float32Array(Math.round(r.seconds * sampleRate));
  const toeAt = r.toeDelayS * (0.85 + rng() * 0.3); // nobody's gait is a metronome
  for (const [at, level] of [[0, 1], [toeAt, r.toeLevel * (0.8 + rng() * 0.4)]] as const) {
    const start = Math.round(at * sampleRate);
    const w = 2 * Math.PI * r.thumpHz * (0.94 + rng() * 0.12) / sampleRate;
    for (let i = start; i < out.length; i++) {
      const t = (i - start) / sampleRate;
      if (t > r.thumpDecayS * 10) break;
      out[i] += Math.sin(w * (i - start)) * r.thumpAmp * level * Math.exp(-t / r.thumpDecayS);
    }
    const clickN = Math.round(sampleRate * 0.0012);
    for (let i = 0; i < clickN && start + i < out.length; i++) out[start + i] += (rng() * 2 - 1) * r.clickAmp * level * (1 - i / clickN);
    for (const b of r.noise) bandNoise(out, sampleRate, { ...b, amp: b.amp * level, delayS: at + (b.delayS ?? 0) }, rng);
  }
  if (r.grains) {
    const a = 1 - Math.exp(-2 * Math.PI * Math.min(r.grains.highHz, sampleRate * 0.45) / sampleRate);
    for (let g = 0; g < r.grains.count; g++) {
      const at = Math.round(rng() * r.grains.spanS * sampleRate);
      const len = Math.round(sampleRate * (0.0015 + rng() * 0.0035));
      const amp = r.grains.amp * (0.35 + rng() * 0.65);
      let lp = 0;
      for (let i = 0; i < len && at + i < out.length; i++) { lp += a * ((rng() * 2 - 1) - lp); out[at + i] += lp * amp * (1 - i / len); }
    }
  }
  return normalize(declick(out, sampleRate), 0.6);
}

/** `foot_<surface>` → the surface ('' = not a footfall key). */
export function proceduralFootSurfaceFor(key: string): FootSurface | '' {
  const m = /^foot_([a-z]+)$/.exec(key);
  return m && (FOOT_SURFACES as readonly string[]).includes(m[1]) ? (m[1] as FootSurface) : '';
}

/** Which synthesized material stands in for a missing impact clip ('' = none). */
export function proceduralMaterialFor(key: string): string {
  const m = /^sfx_impact_(metal|stone|soil|crystal|body)$/.exec(key);
  return m ? m[1] : '';
}

/** The interface click: two short damped sines an octave apart — soft, dry, 45 ms. */
export function synthClick(sampleRate: number): Float32Array {
  const out = new Float32Array(Math.round(sampleRate * 0.045));
  for (let i = 0; i < out.length; i++) {
    const t = i / sampleRate;
    out[i] = (Math.sin(2 * Math.PI * 1320 * t) * 0.6 + Math.sin(2 * Math.PI * 2640 * t) * 0.25) * Math.exp(-t / 0.008);
  }
  return normalize(declick(out, sampleRate), 0.5);
}

/**
 * A room's late reverberation as an impulse response: exponentially decaying
 * noise whose highs die faster than its lows (`damping`), after a pre-delay.
 * Stereo by two decorrelated noise streams. Energy-normalised so every recipe
 * returns at a comparable level — the wet GAIN is the mix control, not the IR.
 */
export function synthImpulseResponse(sampleRate: number, seconds: number, damping: number, preDelayMs: number, density: number, seed = 11): [Float32Array, Float32Array] {
  const n = Math.round(sampleRate * (seconds + preDelayMs / 1000));
  const pre = Math.round(sampleRate * preDelayMs / 1000);
  const chans: [Float32Array, Float32Array] = [new Float32Array(n), new Float32Array(n)];
  for (let c = 0; c < 2; c++) {
    const rng = prng(seed + c * 101);
    const out = chans[c];
    let lp = 0;
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / sampleRate;
      const env = Math.exp(-6.91 * t / seconds); // −60 dB at `seconds`
      // The cutoff falls as the tail ages: the highs are absorbed first.
      const cutoff = 14000 * Math.exp(-damping * 4 * t / seconds) + 500;
      const a = 1 - Math.exp(-2 * Math.PI * cutoff / sampleRate);
      const sample = rng() < density ? rng() * 2 - 1 : 0;
      lp += a * (sample - lp);
      out[i] = lp * env;
    }
    let energy = 0;
    for (let i = 0; i < n; i++) energy += out[i] * out[i];
    const k = energy > 1e-12 ? 1 / Math.sqrt(energy) : 0;
    for (let i = 0; i < n; i++) out[i] *= k;
  }
  return chans;
}
